import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { useEscapeKey } from '../../app/hooks/useEscapeKey';
import { soundManager } from '../../utils/soundManager';
import { hapticManager } from '../../utils/hapticManager';
interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'info';
  onConfirm: () => void;
  onCancel: () => void;
}
const VARIANT_STYLES = {
  danger: {
    iconBg: 'bg-rose-100',
    iconColor: 'text-rose-600',
    confirmClass: 'bg-rose-600 hover:bg-rose-700 text-white'
  },
  warning: {
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    confirmClass: 'bg-amber-600 hover:bg-amber-700 text-white'
  },
  info: {
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    confirmClass: 'bg-blue-600 hover:bg-blue-700 text-white'
  }
};
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Bevestigen',
  cancelLabel = 'Annuleren',
  variant = 'danger',
  onConfirm,
  onCancel
}: ConfirmDialogProps) {
  const styles = VARIANT_STYLES[variant];
  // Close on Escape
  useEscapeKey(onCancel, open);
  // F-FEEL-1 + F-FEEL-2: signal a destructive decision moment when the
  // danger variant opens — silent for info/warning so we don't over-fire.
  useEffect(() => {
    if (open && variant === 'danger') {
      soundManager.playError();
      hapticManager.hapticError();
    }
  }, [open, variant]);
  return <AnimatePresence data-id="element-2349" data-mp="soxd89w9g6en">
      {open && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50" onClick={onCancel} data-id="element-2350" data-mp="omsljgpdhjun" />

          {/* Dialog */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="confirm-dialog-title" data-id="element-2351" data-mp="3vgadfo61hhij">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 relative" data-id="element-2352" data-mp="ry8xssubz2ht">
              <button onClick={onCancel} className="absolute top-4 right-4 p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors" aria-label="Sluiten" data-id="element-2353" data-mp="2oug5hmxg49ah">
                <X className="w-4 h-4" data-id="element-2354" data-mp="2hgysr8kglrzd" />
              </button>

              <div className="flex items-start gap-4" data-id="element-2355" data-mp="2hfnqjd6nwsad">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${styles.iconBg}`} data-id="element-2356" data-mp="2cjfck347b54s">
                  <AlertTriangle className={`w-6 h-6 ${styles.iconColor}`} data-id="element-2357" data-mp="3571fra9io7r4" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-2358" data-mp="307ym0lyzlnyg">
                  <h3 id="confirm-dialog-title" className="text-lg font-bold text-slate-900 mb-1" data-id="element-2359" data-mp="3099qko09azqm">
                    {title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed" data-id="element-2360" data-mp="2r6qoi64aor5f">
                    {message}
                  </p>
                </div>
              </div>

              <div className="flex gap-3 justify-end mt-6" data-id="element-2361" data-mp="1frvc6qt4f1s1">
                <Button variant="secondary" onClick={onCancel} className="text-sm" data-id="element-2362" data-mp="1fuhjqqg0hha9">
                  {cancelLabel}
                </Button>
                <button onClick={onConfirm} className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors ${styles.confirmClass}`} data-id="element-2363" data-mp="2tqn25h78ms9f">
                  {confirmLabel}
                </button>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}